import { z } from 'zod';
/**
 * Return value of `ChannelAdapter.sendOutbound`. Captures delivery status and
 * any provider-assigned identifiers so the core can persist thread references.
 *
 * Modelled as a `z.discriminatedUnion('status', …)` so illegal combinations
 * (e.g. `status: 'failed'` with a `nativeMessageId`, or `status: 'queued'` +
 * `sentAt`) are rejected at the schema boundary rather than leaking into the
 * DB. Call sites switch on `receipt.status` and TS narrows each branch.
 */
/**
 * Queued outbound — the send has been enqueued but not dispatched yet. No
 * provider ids or timestamps exist at this point. Emitted by adapters that
 * defer actual delivery to a background worker.
 */
export const QueuedReceiptSchema = z.object({
    status: z.literal('queued'),
});
/**
 * Successful send — provider returned native ids and (best-effort) the RFC
 * 5322 `Message-ID` the provider stamped. `error` is forbidden.
 *
 * `nativeThreadId` is nullable because some channels (e.g. web_form reply)
 * don't use a threading concept; `nativeMessageId` is required because
 * without it we can't dedupe inbound echoes of the outbound message.
 */
export const SentReceiptSchema = z.object({
    status: z.literal('sent'),
    nativeMessageId: z.string().min(1),
    nativeThreadId: z.string().nullable(),
    /**
     * RFC 5322 `Message-ID` header that the outbound adapter stamped on the sent
     * email (for email channels). Lets the Threading v2 resolver thread inbound
     * replies via the RFC chain (`In-Reply-To`/`References`) when the provider's
     * `threadId` hasn't propagated yet or is missing. Optional — adapters that
     * cannot recover the header leave it undefined. Stored by
     * `MessageStorePg.updateDeliveryStatus` into `message.rfc_message_id` so
     * future step-2 lookups hit.
     */
    rfcMessageId: z.string().optional(),
    sentAt: z.string().datetime({ offset: true }),
});
/**
 * Failed send — only an `error` is present. Native ids and `sentAt` are
 * forbidden so downstream code cannot accidentally persist a `sent` provider
 * id alongside a failure.
 */
export const FailedReceiptSchema = z.object({
    status: z.literal('failed'),
    error: z.object({
        code: z.string().min(1).optional(),
        message: z.string(),
        retryable: z.boolean(),
    }),
});
export const SendReceiptSchema = z.discriminatedUnion('status', [
    QueuedReceiptSchema,
    SentReceiptSchema,
    FailedReceiptSchema,
]);
//# sourceMappingURL=send-receipt.js.map